import type { PostHog } from 'posthog-js';

/**
 * Custom (headless) exit-survey plumbing.
 *
 * PostHog's built-in survey renderer refuses to load under
 * `cookieless_mode: 'always'` (it wants to persist seen/dismissed state on the
 * device, which the cookieless design forbids), so this app fetches the survey
 * definition from the public surveys endpoint and renders it itself — see
 * pages/Landing/ExitSurveyPopover.tsx. Survey content, translations, targeting
 * URL and popup delay all stay editable in PostHog with no deploy needed.
 *
 * The capture payloads below mirror posthog-js's own `survey shown/dismissed/
 * sent` property shapes so responses appear natively in the PostHog surveys UI.
 * The one deliberate omission is the `$set` person-property posthog-js adds:
 * with `person_profiles: 'identified_only'` a `$set` would force a person
 * profile into existence for an anonymous cookieless visitor.
 */

export interface ExitSurveyQuestionTranslation {
  question?: string;
  choices?: string[];
}

export interface ExitSurveyQuestion {
  id: string;
  type: string;
  question: string;
  choices: string[];
  hasOpenChoice?: boolean;
  shuffleOptions?: boolean;
  translations?: Record<string, ExitSurveyQuestionTranslation> | null;
}

export interface ExitSurveyTranslation {
  thankYouMessageHeader?: string;
  thankYouMessageDescription?: string;
  submitButtonText?: string;
}

export interface ExitSurveyAppearance {
  backgroundColor?: string;
  borderColor?: string;
  borderRadius?: string;
  boxShadow?: string;
  textColor?: string;
  textSubtleColor?: string;
  inputBackground?: string;
  placeholder?: string;
  submitButtonText?: string;
  submitButtonColor?: string;
  submitButtonTextColor?: string;
  surveyPopupDelaySeconds?: number;
  zIndex?: string;
  displayThankYouMessage?: boolean;
  thankYouMessageHeader?: string;
}

export interface ExitSurvey {
  id: string;
  name: string;
  type: string;
  start_date?: string | null;
  end_date?: string | null;
  current_iteration?: number | null;
  current_iteration_start_date?: string | null;
  internal_targeting_flag_key?: string | null;
  questions: ExitSurveyQuestion[];
  appearance?: ExitSurveyAppearance | null;
  conditions?: { url?: string; urlMatchType?: string } | null;
  translations?: Record<string, ExitSurveyTranslation> | null;
}

export interface LocalizedExitSurvey {
  survey: ExitSurvey;
  /** The translation key that matched (e.g. 'it'), or null for the base copy. */
  language: string | null;
}

/**
 * Case-insensitive exact match first, then the bare language. This app's
 * locales are <country>-<LANGUAGE> (be-FR is Belgian FRENCH, not BCP 47's
 * Belarusian-in-France), so the language fallback is the SECOND segment:
 * be-FR matches a survey translation keyed 'fr', de-DE matches 'de'.
 */
export const matchTranslationKey = (
  keys: string[] | undefined,
  locale: string | undefined,
): string | null => {
  if (!keys?.length || !locale) {
    return null;
  }
  const wanted = locale.toLowerCase();
  const exact = keys.find((key) => key.toLowerCase() === wanted);
  if (exact) {
    return exact;
  }
  const segments = wanted.split('-');
  const language = segments[segments.length - 1];
  return keys.find((key) => key.toLowerCase() === language) ?? null;
};

export const localizeExitSurvey = (
  survey: ExitSurvey,
  locale: string | undefined,
): LocalizedExitSurvey => {
  const surveyKey = matchTranslationKey(Object.keys(survey.translations ?? {}), locale);
  const questionKey = matchTranslationKey(
    Object.keys(survey.questions[0]?.translations ?? {}),
    locale,
  );
  const language = surveyKey ?? questionKey;
  if (!language) {
    return { survey, language: null };
  }

  const surveyT = surveyKey ? survey.translations?.[surveyKey] : undefined;
  const questions = survey.questions.map((question) => {
    const t = questionKey ? question.translations?.[questionKey] : undefined;
    if (!t) {
      return question;
    }
    return {
      ...question,
      question: t.question ?? question.question,
      // Only take a translated choice list that maps one-to-one, so a stale
      // translation can never drop or invent an answer option.
      choices: t.choices?.length === question.choices.length ? t.choices : question.choices,
    };
  });

  return {
    survey: {
      ...survey,
      questions,
      appearance: {
        ...survey.appearance,
        thankYouMessageHeader:
          surveyT?.thankYouMessageHeader ?? survey.appearance?.thankYouMessageHeader,
        submitButtonText: surveyT?.submitButtonText ?? survey.appearance?.submitButtonText,
      },
    },
    language,
  };
};

/**
 * Evaluates a survey's URL display condition against the page address. Two
 * match types are honoured — "contains" (case-insensitive substring) and
 * "regex" (so one survey's rule in PostHog can express brand AND country
 * combinations, e.g. `(candy|beko)-reg\..*[?&]lang=(it-IT|es-ES)`, and
 * rollout becomes a PostHog edit, not a deploy). Any other match type, and
 * any invalid regex, is treated as not matching rather than guessing —
 * a broken rule must fail closed, never show the survey everywhere.
 */
export const surveyUrlMatches = (survey: ExitSurvey, href: string): boolean => {
  const url = survey.conditions?.url;
  if (!url) {
    return true;
  }
  const matchType = survey.conditions?.urlMatchType ?? 'icontains';
  if (matchType === 'icontains') {
    return href.toLowerCase().includes(url.toLowerCase());
  }
  if (matchType === 'regex') {
    try {
      return new RegExp(url).test(href);
    } catch {
      return false;
    }
  }
  return false;
};

/**
 * Fetches the survey definitions from PostHog's public endpoint (the same one
 * posthog-js uses) and returns the first launched popover survey whose URL
 * condition and internal targeting flag match, localized for `locale`.
 */
export const fetchActiveExitSurvey = async (
  posthog: PostHog,
  locale: string | undefined,
): Promise<LocalizedExitSurvey | null> => {
  const { VITE_POSTHOG_API_KEY, VITE_POSTHOG_HOST } = import.meta.env;
  if (!VITE_POSTHOG_API_KEY || !VITE_POSTHOG_HOST) {
    return null;
  }
  const response = await fetch(`${VITE_POSTHOG_HOST}/api/surveys/?token=${VITE_POSTHOG_API_KEY}`);
  if (!response.ok) {
    return null;
  }
  const { surveys } = (await response.json()) as { surveys?: ExitSurvey[] };
  const match = surveys?.find(
    (survey) =>
      survey.type === 'popover' &&
      !!survey.start_date &&
      !survey.end_date &&
      survey.questions?.length > 0 &&
      surveyUrlMatches(survey, window.location.href) &&
      (!survey.internal_targeting_flag_key ||
        !!posthog.isFeatureEnabled(survey.internal_targeting_flag_key)),
  );
  return match ? localizeExitSurvey(match, locale) : null;
};

const submissionId = (): string =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const baseEventProperties = ({ survey, language }: LocalizedExitSurvey) => ({
  $survey_id: survey.id,
  $survey_name: survey.name,
  $survey_iteration: survey.current_iteration ?? null,
  $survey_iteration_start_date: survey.current_iteration_start_date ?? null,
  ...(language ? { $survey_language: language } : {}),
});

export const surveyShownProperties = baseEventProperties;

export const surveyDismissedProperties = (localized: LocalizedExitSurvey) => ({
  ...baseEventProperties(localized),
  $survey_partially_completed: false,
});

/**
 * `survey sent` payload in posthog-js's format: `$survey_questions` plus the
 * per-question `$survey_response_<id>` key and the legacy first-question
 * `$survey_response` key. Responses carry the displayed (translated) choice
 * text, disambiguated by `$survey_language` — matching native SDK behaviour.
 */
export const surveySentProperties = (
  localized: LocalizedExitSurvey,
  response: string | string[],
) => {
  const question = localized.survey.questions[0];
  // Each key gets its OWN copy of an array response: posthog-js's capture()
  // deep-copies properties with a seen-object guard that silently drops any
  // key whose value object it has already copied, so sharing one array across
  // the three keys loses two of them (PostHog's own SDK copies per key too).
  const copy = () => (Array.isArray(response) ? [...response] : response);
  return {
    ...baseEventProperties(localized),
    $survey_submission_id: submissionId(),
    $survey_completed: true,
    $survey_questions: [{ id: question.id, question: question.question, response: copy() }],
    [`$survey_response_${question.id}`]: copy(),
    $survey_response: copy(),
  };
};
